import {
  createNewOrderToServer,
  editOrderToServer,
  addToExistingOrderToServer,
  prepareOrderToServer,
  validateAvailableInventoryToServer,
} from "./HttpClient";

export const proceedOrderToServer = async (sessionToken, orderSummary, storeContext = null) => {
  try {
    const response = await createNewOrderToServer(sessionToken, orderSummary, storeContext);
    
    // Validate that we have a successful response with required data
    if (!response) {
      throw new Error("No response received from server");
    }

    // Handle inventory conflict — pass through for frontend handling
    if (response.error === "INVENTORY_CONFLICT") {
      return {
        success: false,
        data: response,
      };
    }

    // Check if this is a partial success (207) - order created but notifications failed
    if (response.notificationStatus && response.notificationStatus.errors?.length > 0) {
      console.warn("Order created but notifications failed:", response.notificationStatus);
      return {
        success: true,
        order: response.order,
        data: response,
        partialSuccess: true,
        notificationStatus: response.notificationStatus
      };
    }

    if (!response.order) {
      throw new Error("Order creation failed - no order data in response");
    }

    // Return the actual response data for validation
    return {
      success: true,
      order: response.order,
      data: response,
      partialSuccess: false
    };
  } catch (error) {
    console.error("proceedOrderToServer failed:", error);
    
    // Enhanced error handling for different failure types
    let errorMessage = error.message || "Order creation failed";
    let isRetryable = true;
    let errorType = 'UNKNOWN';
    
    if (error.response) {
      const status = error.response.status;
      const errorData = error.response.data;
      
      if (status >= 400 && status < 500) {
        // Client errors (4xx) - usually not retryable
        isRetryable = false;
        errorType = 'CLIENT_ERROR';
        errorMessage = errorData.message || `Client error (${status})`;
      } else if (status >= 500) {
        // Server errors (5xx) - usually retryable
        isRetryable = true;
        errorType = 'SERVER_ERROR';
        errorMessage = errorData.message || `Server error (${status})`;
      }
    } else if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
      errorType = 'NETWORK_ERROR';
      isRetryable = true;
      errorMessage = "Network connection failed. Please check your internet connection.";
    }
    
    return {
      success: false,
      error: errorMessage,
      errorType: errorType,
      isRetryable: isRetryable,
      originalError: error
    };
  }
};
export const proceedEditOrderToServer = async (sessionToken, orderSummary, storeContext = null) => {
  try {
    const response = await editOrderToServer(sessionToken, orderSummary, storeContext);
    
    // Validate that we have a successful response with required data
    if (!response) {
      throw new Error("No response received from server");
    }
    
    if (!response.order) {
      throw new Error("Order edit failed - no order data in response");
    }
    
    // Return the actual response data for validation
    return {
      success: true,
      order: response.order,
      data: response
    };
  } catch (error) {
    console.error("proceedEditOrderToServer failed:", error);
    return {
      success: false,
      error: error.message || "Order edit failed"
    };
  }
};

export const proceedAddToExistingOrderToServer = async (sessionToken, orderSummary, storeContext = null) => {
  try {
    const response = await addToExistingOrderToServer(sessionToken, orderSummary, storeContext);
    
    // Validate that we have a successful response with required data
    if (!response) {
      throw new Error("No response received from server");
    }
    
    if (!response.order) {
      throw new Error("Order addition failed - no order data in response");
    }
    
    // Return the actual response data for validation
    return {
      success: true,
      order: response.order,
      data: response
    };
  } catch (error) {
    console.error("proceedAddToExistingOrderToServer failed:", error);
    return {
      success: false,
      error: error.message || "Order addition to existing order failed"
    };
  }
};

export const prepareOrder = async (
  sessionToken,
  cartItems,
  charges,
  routeCode,
  orderProfile,
  storeContext = null
) => {
  const data = await prepareOrderToServer(
    sessionToken,
    cartItems,
    charges,
    routeCode,
    orderProfile,
    storeContext
  );

  if (!data || Object.keys(data).length === 0) {
    console.log("No record for this man.");
    return [];
  }

  let chargeList = [];

  if (data.chargeList && data.chargeList.length > 0) {
    chargeList = data.chargeList.map((charge) => ({
      id: charge.id,
      chargetype: charge.chargetype,
      chargeamount: charge.chargeamount,
      chargedescription: charge.description,
      requesttitle: charge.RequestTitle,
    }));
  }

  const referenceRoute = data.referenceRoute[0];

  const settings = data.settings;

  return {
    chargeList,
    referenceRoute,
    settings,
  };
};

export const validateAvailableInventory = async (
  sessionToken,
  cartItems,
  existingOrderCartItems,
  storeContext = null
) => {
  const data = await validateAvailableInventoryToServer(
    sessionToken,
    cartItems,
    existingOrderCartItems,
    storeContext
  );

  if (!data) {
    // statusText === 'Created'
    //
    throw new Error("No inventory found.");
  }

  const updatedCartItems = data
    .map((item) => {
      const updatedQuantity = Math.min(
        parseInt(item.availableQty),
        parseInt(item.quantity)
      );

      return {
        ...item,
        quantity: updatedQuantity,
      };
    })
    .filter((item) => item.quantity > 0);

  const unavailableItems = data.filter(
    (item) => parseInt(item.quantity) > parseInt(item.availableQty)
  );

  const isAvailable = unavailableItems.length === 0;

  const itemsToRemove = unavailableItems.map((item) => {
    const updatedQuantity =
      parseInt(item.quantity) - parseInt(item.availableQty);
    return {
      ...item,
      quantity: updatedQuantity,
    };
  });

  return {
    isAvailable,
    updatedCartItems,
    itemsToRemove,
  };
};
